import { resolveLocalizedText } from '@apruv/survey-contract/localization';
import { paymentCurrency } from './paymentCurrency';
import { startsWithCollapsedTip } from './tipPresentation';
import paymentCopy from './payment-copy.json';
import { evaluateFlagGuards } from '@apruv/survey-contract/flags';
import type { CompiledSurvey, SurveyRuntimeContext } from '@apruv/survey-contract/compiler';
import blockDefaults from '@apruv/survey-contract/block-defaults';
import type { CXPublicSurveyResponse } from '../../services/cxPublicSurveyService';
import { bindPublicRuntimeContext } from '../../services/cxRuntimeContext';
import { surveyPresentationPrimitives } from '../customerSurveyFlow';
import type {
  FlowConfig,
  FlowScreenConfig,
  FlowActionType,
  ActionButtonComponentConfig,
} from '../types';
import { canonicalExecutionPolicy } from './canonicalSurveyContext';
import {
  canonicalComponentConfig,
  selectedSurveyMode,
  visiblePresentationComponents,
} from './surveyPresentation';

/** Assemble presentation from canonical screens only. Retained customerSurvey is never a source. */
export function canonicalSurveyToFlow(
  compiled: CompiledSurvey,
  response: CXPublicSurveyResponse,
  context: SurveyRuntimeContext,
  ratingComponentId?: string
): FlowConfig {
  const doc = compiled.snapshot;
  if (doc.configuration.layout.mode !== 'paged')
    throw new Error('This survey layout is not supported by the paged renderer.');
  const mode = selectedSurveyMode(compiled, context);
  const policy = canonicalExecutionPolicy(response);
  const paymentPreview = policy.preview === true || response.testMode === true;
  // Test invitations may suppress charging, but an authoring scenario must still
  // reflect the selected eligibility. This preview adapter is Stripe-only.
  const showPaymentPreview = paymentPreview && (!policy.preview || policy.tip);
  const localization = doc.configuration.localization;
  const locale =
    typeof context.locale === 'string' && localization.supportedLocales.includes(context.locale)
      ? context.locale
      : localization.defaultLocale;
  const pages = doc.screens.map((screen) => ({
    key: screen.id,
    name: screen.name,
    components: visiblePresentationComponents(compiled, screen.id, context)
      .filter(
        (component) =>
          !(
            screen.name === 'Recovery confirmation' &&
            screen.presentation === 'confirmation_card' &&
            component.type === 'visual_asset' &&
            component.config?.preset === 'success_check' &&
            !component.config.url &&
            !component.config.animation
          )
      )
      .map((component) => {
        const copy = structuredClone(component);
        const baseTranslation = copy.translations?.[localization.defaultLocale];
        const translation = copy.translations?.[locale];
        const defaults = blockDefaults.blocks[copy.type as keyof typeof blockDefaults.blocks];
        copy.config = {
          ...defaults?.config,
          ...copy.config,
          ...baseTranslation?.config,
          ...translation?.config,
        } as typeof copy.config;
        // Keep an absent review style distinct from an explicitly authored filled style.
        // The canonical renderer defaults Google to outlined and other providers to filled.
        if (copy.type === 'review_destinations')
          copy.config.buttonStyle = translation?.config?.buttonStyle ??
            baseTranslation?.config?.buttonStyle ?? component.config?.buttonStyle;
        for (const field of ['content', 'supportingText', 'label'] as const) {
          const key = `component/${encodeURIComponent(copy.id)}/${field}`;
          copy[field] =
            translation?.[field] ??
            localization.translations[locale]?.[key] ??
            baseTranslation?.[field] ??
            localization.translations[localization.defaultLocale]?.[key] ??
            copy[field];
        }
        if (copy.type === 'top_bar' && copy.config?.logoMode === 'organization')
          copy.config.logoUrl = doc.configuration.branding.logoUrl ?? '';
        return copy;
      }),
  }));
  const survey = {
    branding: {
      ...doc.configuration.branding,
      fontFamily:
        doc.configuration.theme.fontFamily === 'inherit'
          ? doc.configuration.branding.fontFamily
          : doc.configuration.theme.fontFamily,
    },
    paths: [
      {
        id: doc.identity.experienceVersionId,
        pages,
        experience: { cssTokens: doc.configuration.tokens, ratingType: mode },
        reviewRouting: response.runtimeContext && Object.prototype.hasOwnProperty.call(response.runtimeContext, 'reviewRouting')
          ? {
              ...doc.configuration.reviewRouting,
              allocations: response.runtimeContext.organizationId === doc.identity.organizationId &&
                (policy.external || policy.preview) && response.runtimeContext.reviewRouting?.enabled === true &&
                Array.isArray(response.runtimeContext.reviewRouting.destinations)
                ? response.runtimeContext.reviewRouting.destinations.flatMap((destination) => {
                    if (
                          !destination || typeof destination.url !== 'string' || !['google', 'yelp', 'facebook', 'bbb'].includes(destination.provider)
                        )
                          return [];
                    try {
                      const url = new URL(destination.url);
                      if (
                            !['https:', 'http:'].includes(url.protocol) || !url.hostname || url.username ||
                            url.password
                          )
                            return [];
                    } catch { return []; }
                    return [
                          {
                            id: destination.id, provider: destination.provider,
                      destinationSource: 'integration', overrideReviewUrl: destination.url,
                      overrideLocationLabel: destination.label ?? '', weight: 1,
                          },
                        ];
                      })
                : [],
            }
          : doc.configuration.reviewRouting,
        recovery: doc.configuration.recoveryPolicy,
      },
    ],
  };
  const bound = bindPublicRuntimeContext({ ...response, customerSurvey: survey });
  const boundSurvey = bound.customerSurvey as typeof survey;
  const path = boundSurvey.paths[0];
  // The legacy runtime binder removes unavailable payment widgets. Keep only a
  // denied canonical action's continuation, in its original position; its provider
  // widget is never mounted by the renderer.
  path.pages.forEach((page, index) => {
    const boundComponents = new Map(page.components.map((component) => [component.id, component]));
    page.components = pages[index].components.flatMap((component) => {
      const boundComponent = boundComponents.get(component.id);
      if (boundComponent) return [boundComponent];
      if (showPaymentPreview && doc.configuration.tipping.enabled && component.type === 'tip_section')
        return [component];
      if (
        ['tip_section', 'finix_tip_section'].includes(component.type) &&
        component.actionIds.length === 1 &&
        !evaluateFlagGuards(doc, 'action', component.actionIds[0], context.resolvedFlags).allowed
      )
        return [component];
      return [];
    });
  });
  const primitives = surveyPresentationPrimitives;
  const tokens = {
    ...doc.configuration.content.variableFallbacks,
    ...Object.fromEntries(
      Object.entries(primitives.surveyTokenValues(bound, boundSurvey)).filter(
        ([, value]) => value !== ''
      )
    ),
  };
  const localized = (path: string, fallback: string) =>
    primitives.resolveTokens(
      localization.translations[locale]?.[`configuration/${path}`] ??
        localization.translations[localization.defaultLocale]?.[`configuration/${path}`] ??
        fallback,
      tokens
    );
  const contactPhone = response.runtimeContext?.contactPhone?.trim() ?? '';
  const canContact = Boolean(
    response.runtimeContext?.capabilities?.canContact &&
      /^\+?[\d\s().-]+$/.test(contactPhone) &&
      /\d/.test(contactPhone)
  );
  const contactAllowed = (policy.external || policy.preview === true) && canContact;
  const hasWebDestination = (value?: string) => {
    try {
      return ['http:', 'https:'].includes(new URL(value ?? '').protocol);
    } catch {
      return false;
    }
  };
  const executableButton = (button: ActionButtonComponentConfig): boolean => {
    if (button.canonicalActions?.length) return button.canonicalActions.some(executableButton);
    const action = doc.actions.find((item) => item.id === button.canonicalActionId);
    // A denied action's configured bypass remains an executable continuation.
    if (action && !evaluateFlagGuards(doc, 'action', action.id, context.resolvedFlags).allowed)
      return true;
    if (action?.destinationReference)
      return (
        contactAllowed &&
        action.destinationReference === 'organization.phone' &&
        ['contact', 'callback'].includes(button.action)
      );
    if (['contact', 'callback'].includes(button.action))
      return button.phone ? contactAllowed : policy.external && hasWebDestination(button.url);
    if (['review', 'referral'].includes(button.action))
      return (
        (policy.external || (policy.preview && button.action === 'referral')) &&
        hasWebDestination(button.url)
      );
    if (button.action === 'edit_response') {
      if (context.response_editing_enabled === false) return false;
      const owner = doc.screens
        .flatMap((screen) => screen.components)
        .find((item) => item.id === ratingComponentId);
      if (owner && canonicalComponentConfig(compiled, owner, context)?.allowResubmit === false)
        return false;
    }
    if (button.action === 'tip') return policy.tip && doc.configuration.tipping.enabled;
    return true;
  };
  const screens: FlowScreenConfig[] = path.pages.map((page, index) => {
    // Keep callback preservation and footer continuation on the same ownership
    // and reference rules as executable canonical actions.
    const permitsReviewAction = (componentId: string, type: string) => {
      const owner = page.components.find((item) => item.id === componentId);
      const actions = doc.actions.filter((action) => owner?.actionIds.includes(action.id));
      if (!actions.length) return true;
      const matching = actions.filter((action) => action.type === type);
      if (matching.length !== 1) return false;
      const action = matching[0];
      if (!evaluateFlagGuards(doc, 'action', action.id, context.resolvedFlags).allowed) return false;
      return (
        !action.destinationReference ||
        (action.destinationReference === 'organization.phone' && type === 'callback' && canContact)
      );
    };
    const feedbackIds = page.components
      .filter((component) => ['comment_box', 'tag_selector'].includes(component.type))
      .map((component) => component.id);
    const components = page.components
      .filter(
        (component) =>
          ((policy.tip || (showPaymentPreview && component.type === 'tip_section')) && doc.configuration.tipping.enabled) ||
          !['tip_section', 'finix_tip_section'].includes(component.type) ||
          (component.actionIds.length === 1 &&
            !evaluateFlagGuards(doc, 'action', component.actionIds[0], context.resolvedFlags)
              .allowed)
      )
      .map((component) => {
        let converted = primitives.componentToFlow(component, path, tokens, {
          pageKey: page.key,
          relatedFeedbackComponentIds: feedbackIds,
          brandingLogoUrl: doc.configuration.branding.logoUrl,
        });
        if (paymentPreview && converted?.type === 'tipping')
          converted = { ...converted, checkoutEnabled: false };
        // Referral fallbacks use only an explicitly owned referral command and authored URL.
        const matchedReferral =
          converted?.type === 'reviewSelection' &&
          response.runtimeContext?.organizationId === doc.identity.organizationId &&
          response.runtimeContext?.reviewRouting?.fallback === 'referral' &&
          response.runtimeContext.reviewRouting.enabled === false &&
          Array.isArray(response.runtimeContext.reviewRouting.destinations) &&
          response.runtimeContext.reviewRouting.destinations.length === 0;
        // Preserve the independently authorized callback when a matched review
        // is replaced by referral. Its phone must never inherit the referral URL.
        const matchedCallback = matchedReferral && converted?.type === 'reviewSelection' &&
          contactAllowed && converted.callbackPhone && converted.callbackLabel &&
          permitsReviewAction(component.id, 'callback')
          ? { phone: converted.callbackPhone }
          : undefined;
        const referralActions = doc.actions.filter(
          (action) => component.actionIds.includes(action.id) && action.type === 'referral'
        );
        if (component.type === 'referral_cta' || matchedReferral) {
          const target =
            typeof component.config?.url === 'string'
              ? primitives.resolveTokens(component.config.url, tokens)
              : '';
          let validTarget = false;
          try {
            const url = new URL(target);
            validTarget =
              ['http:', 'https:'].includes(url.protocol) &&
              Boolean(url.hostname) &&
              !url.username &&
              !url.password;
          } catch {
            /* Missing destinations remain unavailable. */
          }
          const referral = referralActions.length === 1 ? referralActions[0] : undefined;
          const referralAllowed = referral && evaluateFlagGuards(
            doc, 'action', referral.id, context.resolvedFlags
          ).allowed;
          // CX Studio's referral block predates canonical action ownership. Treat the
          // explicitly authored block as an internal Apruv referral entry point when
          // no action was attached; never use an authored external URL in this path.
          const studioReferralFallback =
            component.type === 'referral_cta' &&
            component.actionIds.length === 0 &&
            referralActions.length === 0;
          // Off still exposes the owned command's internal bypass, with no external URL.
          const canRenderReferral = referral && (!referralAllowed || (
            validTarget && (policy.external || policy.preview) && !referral.destinationReference
          ));
          if (canRenderReferral || studioReferralFallback) {
            converted = {
              id: component.id,
              type: 'actionButton',
              action: 'referral',
              label:
                referral?.label ??
                (primitives.resolveTokens(component.content ?? '', tokens) || 'Refer a friend'),
              description: component.type === 'referral_cta'
                ? primitives.resolveTokens(component.supportingText ?? '', tokens) || undefined
                : undefined,
              url: studioReferralFallback
                ? '/preview/referral-request'
                : referralAllowed
                  ? target
                  : undefined,
              showArrow: false,
            };
          } else if (component.type === 'referral_cta') {
            return null;
          }
          // An unavailable matched referral must retain the existing review-card
          // path so an independently authorized callback can still be offered.
        }
        if (converted?.type === 'reviewSelection' && component.config?.buttonStyle === undefined)
          delete converted.buttonStyle;
        // Do not invent a standalone proof heading when no copy was authored.
        if (converted?.type === 'proofOfServiceMedia' && !component.content?.trim())
          delete converted.title;
        if (!converted && !['proof_media', 'technician_notes'].includes(component.type))
          throw new Error(`Unsupported canonical component ${component.type}.`);
        if (
          converted?.type === 'actionButton' &&
          component.type === 'callback_cta' &&
          !component.supportingText?.trim()
        ) {
          // Calling a supplied phone number does not create a callback request.
          delete converted.description;
        }
        if (converted?.type === 'actionButton' &&
            ['primary_button', 'secondary_button'].includes(component.type)) {
          const appearance = component.config ?? {};
          converted = {
            ...converted,
            backgroundColor: appearance.buttonBackgroundColor,
            borderColor: appearance.buttonBorderColor,
            borderRadius: appearance.buttonBorderRadius,
            hoverBackgroundColor: appearance.buttonHoverBackgroundColor,
            minHeight: appearance.buttonMinHeight,
            textColor: appearance.buttonTextColor,
          };
        }
        if (converted?.type === 'actionButton' && component.actionIds.length > 0) {
          const sourceButton = converted;
          const buttonActionIds =
            component.type === 'referral_cta' || matchedReferral
              ? [
                  ...referralActions.map((action) => action.id),
                  ...(matchedCallback ? doc.actions.filter(
                    (action) => component.actionIds.includes(action.id) && action.type === 'callback'
                  ).map((action) => action.id) : []),
                ]
              : component.actionIds;
          const buttons = buttonActionIds.map((id) => {
            const ownedAction = doc.actions.find((action) => action.id === id);
            if (!ownedAction) throw new Error('This button command is unavailable.');
            const button = { ...sourceButton };
            if (matchedCallback && ownedAction.type === 'callback') {
              delete button.url;
              delete button.description;
              button.phone = matchedCallback.phone;
            }
            button.action = (
              ownedAction.type === 'submit' ? 'continue' : ownedAction.type
            ) as Exclude<FlowActionType, 'complete'>;
            if (!['contact', 'callback'].includes(button.action)) delete button.phone;
            if (!['contact', 'callback', 'referral', 'review'].includes(button.action)) {
              delete button.phone;
              delete button.url;
              delete button.description;
            }
            button.canonicalActionId = ownedAction.id;
            const labelKey = `action/${encodeURIComponent(ownedAction.id)}/label`;
            button.label = primitives.resolveTokens(
              localization.translations[locale]?.[labelKey] ??
                localization.translations[localization.defaultLocale]?.[labelKey] ??
                ownedAction.label,
              tokens
            );
            if (ownedAction.destinationReference) {
              // Resolve only the invocation-owned capability supported by the public API.
              // Unknown references cannot fall back to an unrelated legacy destination.
              delete button.url;
              delete button.phone;
              if (
                ownedAction.destinationReference === 'organization.phone' &&
                response.runtimeContext?.capabilities?.canContact &&
                typeof response.runtimeContext.contactPhone === 'string'
              ) {
                const phone = response.runtimeContext.contactPhone.trim();
                if (/^\+?[\d\s().-]+$/.test(phone) && /\d/.test(phone))
                  button.phone = phone.replace(/[^\d+]/g, '');
              }
            }
            button.style = ownedAction.style;
            button.actionIcon = ownedAction.icon;
            button.minHeight = ownedAction.size === 'compact' ? 44 : 48;
            button.textAlign = ownedAction.align;
            delete button.targetScreenId;
            return button;
          });
          converted =
            buttons.length > 1 ? { ...buttons[0], canonicalActions: buttons } : buttons[0];
        }
        if (converted?.type === 'npsForm' || converted?.type === 'csatForm') {
          converted.submitPlacement = doc.configuration.layout.stickyActions ? 'sticky' : 'inline';
          const rating = doc.configuration.rating;
          const componentTextAddress = `component/${encodeURIComponent(component.id)}/content`;
          const componentLocales = [locale, localization.defaultLocale];
          const hasScreenPrompt = componentLocales.some(
            (language) =>
              component.translations?.[language]?.content !== undefined ||
              localization.translations[language]?.[componentTextAddress] !== undefined
          );
          const hasScreenConfig = (key: string) =>
            componentLocales.some((language) =>
              Object.prototype.hasOwnProperty.call(
                component.translations?.[language]?.config ?? {},
                key
              )
            );
          converted.editResponseLabel = localized(
            'summary/editLabel',
            converted.editResponseLabel ?? doc.configuration.summary.editLabel
          );
          converted.submittedTitle = localized(
            'summary/title',
            converted.submittedTitle ?? doc.configuration.summary.title
          );
          converted.submittedBody = localized(
            'summary/body',
            converted.submittedBody ?? doc.configuration.summary.body
          );
          const inlineCopy = (field: 'feedbackPrompt' | 'feedbackPlaceholder', fallback: string) => {
            const key = `inline-feedback/${encodeURIComponent(component.id)}/${field}`;
            return Object.prototype.hasOwnProperty.call(doc.configuration.content.text, key)
              ? primitives.resolveTokens(resolveLocalizedText(doc, locale, `text/${encodeURIComponent(key)}/value`) ?? '', tokens)
              : fallback;
          };
          converted.feedbackPrompt = inlineCopy('feedbackPrompt', localized(
            'feedback/title',
            converted.feedbackPrompt ?? doc.configuration.feedback.title
          ));
          converted.feedbackPlaceholder = inlineCopy('feedbackPlaceholder', localized(
            'feedback/placeholder',
            converted.feedbackPlaceholder ?? doc.configuration.feedback.placeholder
          ));

          converted.question = localized(
            'rating/prompt',
            hasScreenPrompt ? (converted.question ?? rating.prompt) : rating.prompt
          );
          const ratingActionLabel = localized(
            'rating/submitLabel',
            hasScreenConfig('actionLabel')
              ? (converted.actionLabel ?? rating.submitLabel)
              : rating.submitLabel
          );
          converted.actionLabel =
            ratingActionLabel === 'Submit and continue' ? 'Submit' : ratingActionLabel;
          if (converted.type === 'npsForm') {
            converted.lowLabel = localized(
              'rating/lowLabel',
              hasScreenConfig('lowLabel') ? (converted.lowLabel ?? rating.lowLabel) : rating.lowLabel
            );
            converted.highLabel = localized(
              'rating/highLabel',
              hasScreenConfig('highLabel')
                ? (converted.highLabel ?? rating.highLabel)
                : rating.highLabel
            );
          } else {
            converted.options = converted.options.map((option) => ({
              ...option,
              label: localized(
                `rating/labels/${encodeURIComponent(String(option.value))}`,
                rating.labels[String(option.value)] ?? option.label
              ),
            }));
          }
        }
        if (
          converted?.type === 'npsForm' ||
          converted?.type === 'csatForm' ||
          converted?.type === 'feedbackForm'
        ) {
          converted.allowResubmit =
            doc.configuration.survey?.responseEditingEnabled !== false &&
            converted.allowResubmit !== false;
        }
        if (converted?.type === 'feedbackForm') {
          const feedback = doc.configuration.feedback;
          converted.title = localized('feedback/title', converted.title ?? feedback.title);
          converted.placeholder = localized(
            'feedback/placeholder',
            converted.placeholder ?? feedback.placeholder
          );
          converted.actionLabel = localized(
            'feedback/submitLabel',
            converted.actionLabel ?? feedback.submitLabel
          );
          converted.submittedTitle = localized(
            'summary/title',
            converted.submittedTitle ?? doc.configuration.summary.title
          );
          converted.submittedBody = localized(
            'summary/body',
            converted.submittedBody ?? doc.configuration.summary.body
          );
        }
        if (converted?.type === 'finixTipping') {
          const tipping = doc.configuration.tipping;
          converted.amounts = tipping.amounts.map(value => ({label: paymentCurrency(value, locale, true), value}));
          converted.allowCustomAmount = tipping.allowCustom;
          converted.minimumAmount = Math.max(1, tipping.minimum);
          converted.maximumAmount = Math.min(100, tipping.maximum);
          converted.tipAmountLabel = localized('tipping/title', tipping.title);
        }
        if (converted?.type === 'tipping') {
          const tipping = doc.configuration.tipping;
          converted.collapsible = tipping.initialDisplay !== undefined;
          converted.initiallyCollapsed = startsWithCollapsedTip(tipping.initialDisplay, response.sourceEvent?.payload);
          converted.paymentLocale = locale;
          const componentTerms: Record<string, string | undefined> = {
            'payment.termsText': converted.termsText,
            'payment.termsLink': converted.termsLinkLabel,
            'payment.termsTitle': converted.termsModalTitle,
          };
          const termFields: Record<string, string> = {
            'payment.termsText': 'termsText',
            'payment.termsLink': 'termsLinkLabel',
            'payment.termsTitle': 'termsModalTitle',
          };
          const translatedTerm = (key: string, language: string) => {
            const value = (
              component.translations?.[language]?.config as Record<string, unknown> | undefined
            )?.[termFields[key]];
            return typeof value === 'string' ? value : undefined;
          };
          converted.paymentCopy = Object.fromEntries(
            Object.entries(paymentCopy).map(([key, fallback]) => [
              key,
              primitives.resolveTokens(
                localization.translations[locale]?.[`text/${encodeURIComponent(key)}/value`] ??
                  translatedTerm(key, locale) ??
                  localization.translations[localization.defaultLocale]?.[
                    `text/${encodeURIComponent(key)}/value`
                  ] ??
                  translatedTerm(key, localization.defaultLocale) ??
                  doc.configuration.content.text[key] ??
                  componentTerms[key] ??
                  fallback,
                tokens
              ),
            ])
          );
          converted.continueLabel = converted.paymentCopy['payment.continue'];
          converted.amounts = tipping.amounts.map((value) => ({
            label: paymentCurrency(value, locale, true),
            value,
          }));
          converted.allowCustomAmount = tipping.allowCustom;
          converted.minimumAmount = Math.max(1, tipping.minimum);
          converted.maximumAmount = tipping.maximum;
          const authoredTippingTitle = page.components.find(
            (candidate) => candidate.type === 'subheader'
          )?.content;
          converted.title =
            tipping.title === 'Leave a tip' && authoredTippingTitle?.trim()
              ? primitives.resolveTokens(authoredTippingTitle, tokens)
              : localized('tipping/title', tipping.title);
          converted.paymentMethodLabels = Object.fromEntries(
            Object.entries(doc.configuration.paymentDisplay.methodLabels).map(([key, value]) => [
              key,
              localized(`paymentDisplay/methodLabels/${encodeURIComponent(key)}`, value),
            ])
          );
          converted.paymentHelpText = localized(
            'paymentDisplay/helpText',
            doc.configuration.paymentDisplay.helpText
          );
          converted.skipLabel = localized('tipping/skipLabel', tipping.skipLabel);
          const terms = localized('tipping/terms', tipping.terms);
          if (terms) {
            converted.termsEnabled = true;
            converted.termsHtml = terms;
          }
        }
        // Public test invitations intentionally have no executable review URLs.
        // Keep authored providers visible as disabled samples instead of an empty page.
        if (response.testMode === true && converted?.type === 'reviewSelection' &&
            doc.configuration.reviewRouting.enabled && permitsReviewAction(converted.id, 'review')) {
          converted.sites = doc.configuration.reviewRouting.allocations.flatMap((allocation) =>
            ['google', 'yelp', 'facebook', 'bbb'].includes(allocation.provider)
              ? [{ id: allocation.id, provider: allocation.provider,
                  label: allocation.provider === 'google' ? 'Google' : allocation.provider === 'yelp' ? 'Yelp' : allocation.provider === 'facebook' ? 'Facebook' : 'BBB',
                  url: '' }]
              : []);
        }
        // Match visible contact controls to the invocation and owned command gates,
        // including blocks that also retain an eligible review destination.
        if (
          converted?.type === 'reviewSelection' &&
          !(contactAllowed && permitsReviewAction(converted.id, 'callback'))
        )
          delete converted.callbackPhone;
        // A denied projection must not leave an empty review prompt. Preserve
        // authored contact actions, and let the normal footer continue the flow.
        if (
          converted?.type === 'reviewSelection' &&
          response.runtimeContext && Object.prototype.hasOwnProperty.call(response.runtimeContext, 'reviewRouting') &&
          !(response.testMode === true && converted.sites.length > 0) &&
          !converted.sites.some((site) => hasWebDestination(site.url)) &&
          !(
            contactAllowed && converted.callbackPhone && converted.callbackLabel && permitsReviewAction(converted.id, 'callback')
          )
        )
          return null;
        if (converted?.type === 'actionButton') delete converted.targetScreenId;
        if (converted?.type === 'backButton') delete converted.targetScreenId;
        return converted;
      })
      .filter((component): component is NonNullable<typeof component> => component !== null);
    return {
      id: page.key,
      previewLabel: page.name,
      components,
      routes: [],
      presentation: doc.screens[index].presentation,
      hideFooterAction: components.some((component) => {
        if (component.type === 'reviewSelection') {
          return (
            (policy.external &&
              component.sites.some((site) => hasWebDestination(site.url)) &&
              permitsReviewAction(component.id, 'review')) ||
            Boolean(
              contactAllowed &&
                component.callbackPhone &&
                component.callbackLabel &&
                permitsReviewAction(component.id, 'callback')
            )
          );
        }
        if (component.type === 'actionButton') return executableButton(component);
        return ['npsForm', 'csatForm', 'tipping', 'finixTipping'].includes(component.type);
      }),
      showBack: false,
    };
  });
  return {
    id: doc.identity.experienceVersionId,
    name: response.module?.name ?? 'Survey',
    brand: primitives.buildBrand(boundSurvey, path, tokens),
    screens,
  };
}
